const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24
const DAYS_PER_WEEK = 7
const MS_PER_HOUR = MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR
const BYTES_PER_KIB = 1024

export interface BadgeColours {
  bg: string
  text: string
}

export const PRIORITY_COLOURS_LIGHT: Record<string, BadgeColours> = {
  critical: { bg: 'rgba(239, 68, 68, 0.12)', text: 'rgb(185, 28, 28)' },
  high: { bg: 'rgba(249, 115, 22, 0.12)', text: 'rgb(194, 65, 12)' },
  medium: { bg: 'rgba(132, 204, 22, 0.12)', text: 'rgb(77, 124, 15)' },
  low: { bg: 'var(--highlight)', text: 'var(--text-dim)' },
}

export const PRIORITY_COLOURS_DARK: Record<string, BadgeColours> = {
  critical: { bg: 'rgba(239, 68, 68, 0.15)', text: 'rgb(248, 113, 113)' },
  high: { bg: 'rgba(249, 115, 22, 0.15)', text: 'rgb(251, 146, 60)' },
  medium: { bg: 'rgba(132, 204, 22, 0.15)', text: 'rgb(163, 230, 53)' },
  low: { bg: 'var(--highlight)', text: 'var(--text-dim)' },
}

export const STATUS_COLOURS_LIGHT: Record<string, BadgeColours> = {
  collection: { bg: 'var(--highlight)', text: 'var(--text-dim)' },
  analysis: { bg: 'rgba(168, 85, 247, 0.12)', text: 'rgb(126, 34, 206)' },
  review: { bg: 'rgba(245, 158, 11, 0.12)', text: 'rgb(180, 83, 9)' },
  reporting: { bg: 'rgba(99, 102, 241, 0.12)', text: 'rgb(67, 56, 202)' },
  closed: { bg: 'var(--highlight)', text: 'var(--text-muted)' },
  archived: { bg: 'var(--highlight)', text: 'var(--text-muted)' },
}

export const STATUS_COLOURS_DARK: Record<string, BadgeColours> = {
  collection: { bg: 'var(--highlight)', text: 'var(--text-dim)' },
  analysis: { bg: 'rgba(168, 85, 247, 0.15)', text: 'rgb(192, 132, 252)' },
  review: { bg: 'rgba(245, 158, 11, 0.15)', text: 'rgb(251, 191, 36)' },
  reporting: { bg: 'rgba(99, 102, 241, 0.15)', text: 'rgb(165, 180, 252)' },
  closed: { bg: 'var(--highlight)', text: 'var(--text-muted)' },
  archived: { bg: 'var(--highlight)', text: 'var(--text-muted)' },
}

export const DEFAULT_BADGE: BadgeColours = { bg: 'var(--highlight)', text: 'var(--text-dim)' }

export type Theme = 'light' | 'dark'

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_KIB))
  const value = parseFloat((bytes / BYTES_PER_KIB ** index).toFixed(1)).toString()
  return `${value} ${sizes[index] ?? ''}`
}

type RelativeTimeT = (key: string, opts?: Record<string, unknown>) => string

export function formatRelativeTime(dateString: string, t: RelativeTimeT): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / MS_PER_HOUR)
  const diffDays = Math.floor(diffHours / HOURS_PER_DAY)

  if (diffHours < 1) return t('time.justNow')
  if (diffHours < HOURS_PER_DAY) return t('time.hoursAgo', { count: diffHours })
  if (diffDays === 1) return t('time.yesterday')
  if (diffDays < DAYS_PER_WEEK) return t('time.daysAgo', { count: diffDays })
  return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })
}

export function detectDashboardTheme(): Theme {
  const el =
    typeof document === 'undefined' ? null : document.querySelector<HTMLElement>('.dashboard')
  return el?.dataset.theme === 'dark' ? 'dark' : 'light'
}
